import React, { useEffect, useState } from 'react'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs'
import { t } from 'i18next'
import { useMutation } from '@apollo/client'
import { UpdateSettings } from '../../../graphql'
import { useAllSettings } from '../../../graphql/settings/storeSettings/AllSettings'
import useStore from '../../../store/useStore'
import SuccessAlert from '../../../components/Alerts/SuccessAlert'
import FailAlert from '../../../components/Alerts/FailAlert'
import LoadingWidget from '../../../components/LoadingWidget/LoadingWidget'
import PermissionsError from '../../../components/Alerts/PermissionsError'

const AdditionalSetting = () => {
  const storeData = useStore((state: any) => state.userData.store);
  const userData = useStore((state: any) => state.userData);

  const [permissions, setPermissions] = useState({
    view: false,
  })

  // Permission configuration
  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      // admin
      if (userData.permissions.length === 0 && userData.role === "admin") {
        setPermissions({
          view: true,
        })

        return
      }

      const filtered = userData?.permissions?.filter((perm: { name: string }) => perm.name === "settings")

      let viewPermission = filtered[0]?.permissions.includes("additional settings")

      setPermissions({
        view: viewPermission || false,
      })

    }
  }, [userData])

  // I did scroll to the top because when the component is render it show the bottom of the page because the layout
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  // i had to use a main state for control loading because of ui issues
  const [initialLoading, setInitialLoading] = useState(true);

  const [options, setOptions] = useState<any>(
    {
      minimumOrders: false,
      neighborhoods: false,
      viewProduct: false,
      giftSystem: false,
      ePaymentFees: false,
      cashPayment: false,
    }
  )

  const [defaultWeight, setDefaultWeight] = useState(1);

  const [commercialRegistrationNumber, setCommercialRegistrationNumber] = useState("");
  const [showCommercialRegistrationNumber, setShowCommercialRegistrationNumber] = useState(false);
  const [language, setLanguage] = useState<string | "en" | "ar">("ar");
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  const [AllSettings, { loading, data: dataAllSettings }] = useAllSettings()

  useEffect(() => {
    storeData &&
      (async () => {
        await AllSettings({
          variables: { idStore: storeData.id }
        })
      })()
  }, [storeData]);

  useEffect(() => {

    const currentOptions = { ...options }

    if (dataAllSettings?.allSettings) {

      currentOptions["minimumOrders"] = dataAllSettings?.allSettings.activeMinimumOrder
      currentOptions["neighborhoods"] = dataAllSettings?.allSettings.activeStreets
      currentOptions["viewProduct"] = dataAllSettings?.allSettings.productPicturesShowOneSize
      currentOptions["giftSystem"] = dataAllSettings?.allSettings.activeGift
      currentOptions["ePaymentFees"] = dataAllSettings?.allSettings.activeElectronicPayment
      currentOptions["cashPayment"] = dataAllSettings?.allSettings.activePayment

      setDefaultWeight(dataAllSettings?.allSettings.defaultWeightProduct)
      setCommercialRegistrationNumber(dataAllSettings?.allSettings.commercialRegistrationNumber)
      setShowCommercialRegistrationNumber(dataAllSettings?.allSettings.showCommercialRegistrationNumber)
      setLanguage(dataAllSettings?.allSettings.language || "ar");
      dataAllSettings?.allSettings.maintenanceMode === null ? setMaintenanceMode(false) : setMaintenanceMode(dataAllSettings?.allSettings.maintenanceMode)
    }

    setOptions(currentOptions);

    setInitialLoading(false)
  }, [dataAllSettings])

  const changeOptions = (option: string) => {
    const currentOptions = { ...options }
    currentOptions[option as string] = !currentOptions[option as string]
    setOptions(currentOptions)
  }

  const [updateSettings, { loading: updateLoading, error }] = useMutation(UpdateSettings);

  const updateOptions = async () => {
    setUpdateIndex(1)
    const currentOptions = { ...options }

    await updateSettings({
      variables: {
        updateSettingsId: dataAllSettings?.allSettings.id,
        content: {
          activeMinimumOrder: currentOptions.minimumOrders,
          activeStreets: currentOptions.neighborhoods,
          productPicturesShowOneSize: currentOptions.viewProduct,
          activeGift: currentOptions.giftSystem,
          activeElectronicPayment: currentOptions.ePaymentFees,
          activePayment: currentOptions.cashPayment,
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  }

  const updateWeight = async () => {
    setUpdateIndex(2);

    await updateSettings({
      variables: {
        updateSettingsId: dataAllSettings?.allSettings.id,
        content: {
          defaultWeightProduct: defaultWeight
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  }

  const updateCommercialRegistrationNumber = async () => {
    setUpdateIndex(3);

    await updateSettings({
      variables: {
        updateSettingsId: dataAllSettings?.allSettings.id,
        content: {
          commercialRegistrationNumber: commercialRegistrationNumber,
          showCommercialRegistrationNumber: showCommercialRegistrationNumber
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  }

  const updateLanguage = async () => {
    setUpdateIndex(4);

    await updateSettings({
      variables: {
        updateSettingsId: dataAllSettings?.allSettings.id,
        content: {
          language: language
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  }

  const updateMaintenanceMode = async () => {
    setUpdateIndex(5);

    await updateSettings({
      variables: {
        updateSettingsId: dataAllSettings?.allSettings.id,
        content: {
          maintenanceMode: maintenanceMode
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  }

  const [updateIndex, setUpdateIndex] = useState(0)

  return (
    <section className='mx-3 my-4 md:mx-7'>
      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      <div className='flex flex-col-reverse md:flex-row md:justify-between md:items-center'>
        <div>
          <h3 className='text-2xl font-semibold'>{t('additionalSettings')}</h3>
        </div>
        <div>
          <Breadcrumbs />
        </div>
      </div>

      {initialLoading || loading?
        <LoadingWidget />
        :
        permissions.view === false ?
          <PermissionsError />
          :
          <>
            {/* {initialLoading || loading ?
              <LoadingWidget />
              :
              <div className='relative p-2 mt-3 bg-white rounded-md'>
                {updateLoading && updateIndex === 1 &&
                  <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
                }
                <span>
                  {t('additionalOptionsForStore')}
                </span>

                <div className='px-3 my-2'>
                  <div>
                    <div className="flex items-center my-2">
                      <input checked={options.minimumOrders || false} type="checkbox" onChange={() => changeOptions("minimumOrders")} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                      <label className="mx-2 font-medium text-gray-800">{t('activeMinimumOrder')}</label>
                      <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center my-2">
                      <input checked={options.neighborhoods || false} type="checkbox" onChange={() => changeOptions("neighborhoods")} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                      <label className="mx-2 font-medium text-gray-800">{t('activateNeighborhoods')}</label>
                      <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center my-2">
                      <input checked={options.viewProduct || false} type="checkbox" onChange={() => changeOptions("viewProduct")} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                      <label className="mx-2 font-medium text-gray-800">{t('viewProductPhotosOutsideOneSize')}</label>
                      <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center my-2">
                      <input checked={options.giftSystem || false} type="checkbox" onChange={() => changeOptions("giftSystem")} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                      <label className="mx-2 font-medium text-gray-800">{t('activateInStoreGiftSystem')}</label>
                      <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center my-2">
                      <input checked={options.ePaymentFees || false} type="checkbox" onChange={() => changeOptions("ePaymentFees")} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                      <label className="mx-2 font-medium text-gray-800">{t('activationOfePaymentfees')}</label>
                      <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center my-2">
                      <input checked={options.cashPayment || false} type="checkbox" onChange={() => changeOptions("cashPayment")} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                      <label className="mx-2 font-medium text-gray-800">{t('activateCashPaymentOption')}</label>
                      <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>

                <div className='flex justify-end pt-5'>
                  <button onClick={() => updateOptions()} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                      <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                    </svg>

                    <span className="mx-2">
                      {t('save')}
                    </span>
                  </button>
                </div>
              </div>
      } */}

            {initialLoading || loading ?
              <LoadingWidget />
              :
              <div className='relative p-2 mt-3 bg-white rounded-md'>
                {updateLoading && updateIndex === 2 &&
                  <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
                }
                <div>
                  <label htmlFor="weight" className="block mb-2 text-sm font-medium text-gray-900">{t('weightProductWhenSendingToTheShippingCompany')}</label>
                  <input type="number" min={1} value={defaultWeight || ""} id="weight" onChange={(e) => setDefaultWeight(parseInt(e.target.value))} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder='Kg.' required />
                </div>

                <div className='flex justify-end pt-5'>
                  <button onClick={() => updateWeight()} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                      <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                    </svg>

                    <span className="mx-2">
                      {t('save')}
                    </span>
                  </button>
                </div>
              </div>
            }

            {initialLoading || loading ?
              <LoadingWidget />
              :
              <div className='relative p-2 mt-3 bg-white rounded-md'>
                {updateLoading && updateIndex === 3 &&
                  <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
                }
                <div>
                  <label htmlFor="commercialNumber" className="block mb-2 text-sm font-medium text-gray-900">{t('commercialRegistrationNumber')}</label>
                  <input type="text" id="commercialNumber" value={commercialRegistrationNumber || ""} onChange={(e) => setCommercialRegistrationNumber(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder='0000000' required />
                </div>
                <div>
                  <div className="flex items-center my-2">
                    <input type="checkbox" checked={showCommercialRegistrationNumber || false} onChange={() => setShowCommercialRegistrationNumber(!showCommercialRegistrationNumber)} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                    <label className="mx-2 font-medium text-gray-800">{t('showCommercialRecord')}</label>
                    <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>

                <div className='flex justify-end pt-5'>
                  <button onClick={() => updateCommercialRegistrationNumber()} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                      <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                    </svg>

                    <span className="mx-2">
                      {t('save')}
                    </span>
                  </button>
                </div>
              </div>
            }

            {initialLoading || loading ?
              <LoadingWidget />
              :
              <div className='relative p-2 mt-3 bg-white rounded-md'>
                {updateLoading && updateIndex === 4 &&
                  <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
                }
                <span>
                  {t('language')}
                </span>
                <div>
                  <div className="flex items-center my-2">
                    <input type="checkbox" onChange={() => setLanguage("en")} checked={language === "en" ? true : false} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                    <label className="mx-2 font-medium text-gray-800">{t('en')}</label>
                    <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="flex items-center my-2">
                    <input type="checkbox" onChange={() => setLanguage("ar")} checked={language === "ar" ? true : false} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                    <label className="mx-2 font-medium text-gray-800">{t('ar')}</label>
                    <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>

                <div className='flex justify-end pt-5'>
                  <button onClick={() => updateLanguage()} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                      <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                    </svg>

                    <span className="mx-2">
                      {t('save')}
                    </span>
                  </button>
                </div>
              </div>
            }

            {initialLoading || loading ?
              <LoadingWidget />
              :
              <div className='relative p-2 mt-3 bg-white rounded-md'>
                {updateLoading && updateIndex === 5 &&
                  <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
                }
                <span>
                  {t('storeMaintenanceMode')}
                </span>
                <div>
                  <div className="flex items-center my-2">
                    <input type="checkbox" checked={maintenanceMode} onChange={() => setMaintenanceMode(!maintenanceMode)} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
                    <label className="mx-2 font-medium text-gray-800">{t('pauseStoreforMaintenance')}</label>
                    <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>

                <div className='flex justify-end pt-5'>
                  <button onClick={() => updateMaintenanceMode()} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                      <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                    </svg>

                    <span className="mx-2">
                      {t('save')}
                    </span>
                  </button>
                </div>
              </div>
            }
          </>}
    </section>
  )
}

export default AdditionalSetting